/**
 * Agentic churn-save playbook engine — trigger enrollment, blast-radius-
 * simulated batch runs, earned per-playbook autonomy, and closed-loop
 * outcome verification.
 *
 * Fixes the decorative-playbook / no-trigger-engine / fire-and-forget /
 * static-autonomy findings in one governed loop:
 *
 *   evaluateTriggers  — declarative compound triggers
 *                       ('churnProbability>=70 AND renewalDays<90') auto-
 *                       enroll at-risk accounts from REAL churn-risk fusion
 *                       results (crm/churnRisk.ts). No risk signal → no
 *                       enrollment; nothing is ever fabricated.
 *   buildPlaybookRun  — assembles the governed save steps (save-email,
 *                       renewal check-in task, exec escalation) as
 *                       REVERSIBLE `create_task` actions ordered by the
 *                       shipped task-graph engine (crm/taskGraph.topoSort).
 *   simulateBatch     — the full write-set (which emails / tasks / contacts
 *                       would be touched) BEFORE anything runs, routed
 *                       through the shipped policy simulator + blast-radius
 *                       replay, with per-account carve-out support.
 *   triageBatch       — ranks steps by blast radius / reversibility /
 *                       confidence so low-risk sends clear in one gesture
 *                       while exec escalations surface first for review.
 *   resolveAutonomy   — the per-account / per-playbook trust ladder: N
 *                       clean ledger approvals earn auto-send with post-hoc
 *                       review; any reversal/edit/reject resets to human.
 *   verifyPlayOutcome — closes the loop via the shipped outcome verifier
 *                       (health_delta contract): did health actually
 *                       recover? A save writes an honest value-ledger
 *                       attribution ('$84,000 ARR retained'); a miss earns
 *                       nothing.
 *   rollupPlaybookEffectiveness — the conversion stat a renewal deck needs
 *                       ('saved 8 of 11').
 *
 * Deliberately PURE: no store, no network, no implicit clock (every
 * timestamp is carried on the inputs). The agent worker is an optional
 * narrator at the UI seam (savePlaybookPanel.tsx); when it is absent the
 * deterministic path produces the same enrollments, simulations, and
 * verdicts — the engine never blocks on a worker.
 */

import { topoSort } from './taskGraph';
import type { ChurnRiskResult } from './churnRisk';
import type { ApprovalItem, ProjectTask } from '../db/types';
import type { CreateTaskAction } from '../agent/types';
import { diffForAction } from '../agent/actionDiff';
import {
  classifyProposal,
  simulatePolicy,
  type ScoredProposal,
  type SimCounts,
} from '../agent/policySimulator';
import {
  blastRadiusSummary,
  computeBlastRadius,
  isSaveBlocked,
  type PolicyDelta,
  type TriageOutcome,
} from '../agent/policyBlastRadius';
import { emptyPolicy, type AutonomyPolicy } from '../agent/autonomyPolicy';
import {
  computeActionClassStats,
  type EarnUpLedgerEntry,
} from '../agent/autonomyEarnUp';
import { buildOutcomeContract } from '../agent/outcomeContract';
import {
  verifyOutcome,
  verdictToLedgerEntry,
  type OutcomeVerdict,
  type OutcomeVerdictEntry,
} from '../agent/outcomeVerifier';
import type { ValueEntry } from '../agent/valueRollup';

const DAY_MS = 86_400_000;

// ===========================================================================
// Playbook + trigger schema
// ===========================================================================

/** The one playbook this engine ships: the churn save. */
export const PLAYBOOK_ID = 'churn-save';

/** A declarative enrollment trigger: a compound AND condition over metrics. */
export interface PlaybookTrigger {
  id: string;
  playbookId: string;
  /** e.g. 'churnProbability>=70 AND renewalDays<90'. */
  condition: string;
}

/** The default churn-save trigger — hot account inside its renewal window. */
export const DEFAULT_SAVE_TRIGGER: PlaybookTrigger = {
  id: 'churn-renewal-window',
  playbookId: PLAYBOOK_ID,
  condition: 'churnProbability>=70 AND renewalDays<90',
};

/** Minimal account shape the engine enrolls — structural, never a store read. */
export interface SaveAccount {
  id: string;
  name: string;
  /** Real ARR/ACV in USD when set — never invented. */
  accountValue?: number;
  /** Epoch ms of the renewal, when known. */
  renewalAt?: number;
}

/** One parsed clause of a compound trigger condition. */
export interface TriggerClause {
  field: string;
  op: '>=' | '<=' | '>' | '<' | '=';
  threshold: number;
}

/** A clause evaluated against a specific account's real metrics. */
export interface MatchedClause extends TriggerClause {
  /** The measured value the clause matched against. */
  actual: number;
}

/**
 * Parse a compound condition ('a>=70 AND b<90') into clauses. Returns null
 * when ANY clause fails to parse — an unparseable trigger can never enroll
 * anyone (safe by construction, not by luck).
 */
export function parseCondition(condition: string): TriggerClause[] | null {
  const parts = condition
    .split(/\s+AND\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const clauses: TriggerClause[] = [];
  for (const part of parts) {
    const m = /^([a-zA-Z][a-zA-Z0-9_]*)\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/.exec(part);
    if (!m) return null;
    clauses.push({
      field: m[1],
      op: m[2] as TriggerClause['op'],
      threshold: Number(m[3]),
    });
  }
  return clauses;
}

/** The metric vocabulary triggers may reference, per enrolled account. */
interface TriggerMetrics {
  churnProbability: number;
  /** Days until renewal; null when no renewal date is known. */
  renewalDays: number | null;
  /** Risk-engine data-sufficiency confidence, 0–1. */
  confidence: number;
  leadTimeDays: number;
  arrAtRisk: number;
}

function metricOf(metrics: TriggerMetrics, field: string): number | null {
  switch (field) {
    case 'churnProbability':
      return metrics.churnProbability;
    case 'renewalDays':
      return metrics.renewalDays;
    case 'confidence':
      return metrics.confidence;
    case 'leadTimeDays':
      return metrics.leadTimeDays;
    case 'arrAtRisk':
    case 'accountValue':
      return metrics.arrAtRisk;
    default:
      return null; // unknown field can never match — never enroll on a typo
  }
}

function clauseHolds(clause: TriggerClause, actual: number): boolean {
  switch (clause.op) {
    case '>=':
      return actual >= clause.threshold;
    case '<=':
      return actual <= clause.threshold;
    case '>':
      return actual > clause.threshold;
    case '<':
      return actual < clause.threshold;
    case '=':
      return actual === clause.threshold;
  }
}

// ===========================================================================
// evaluateTriggers — declarative auto-enrollment
// ===========================================================================

/** One account auto-enrolled into the playbook by a matched trigger. */
export interface PlaybookEnrollment {
  /** Deterministic: `${playbookId}:${accountId}` — re-running never dupes. */
  id: string;
  playbookId: string;
  accountId: string;
  accountName: string;
  triggerId: string;
  condition: string;
  /** Every clause with the measured value it matched on — the audit trail. */
  matched: MatchedClause[];
  churnProbability: number;
  renewalDays: number | null;
  /** Real ARR at risk (0 when the account carries no value — never invented). */
  arrAtRisk: number;
  /** Risk-engine confidence, 0–1. */
  confidence: number;
  /** Epoch ms (the risk result's computedAt unless the caller injects now). */
  enrolledAt: number;
  /** Honest human-readable enrollment reason (deterministic fallback). */
  reason: string;
  /** Where the underlying risk narrative came from. */
  source: 'agent' | 'deterministic';
}

/**
 * Evaluate declarative triggers over accounts × their REAL churn-risk
 * results. Only accounts whose risk result satisfies EVERY clause of a
 * trigger enroll; accounts with no risk result are skipped entirely (no
 * signal → no enrollment — the deterministic fallback is the churn engine
 * itself, never an invented probability). One enrollment per account: the
 * first matching trigger wins. Pure and deterministic.
 */
export function evaluateTriggers(
  accounts: SaveAccount[],
  riskResults: ChurnRiskResult[],
  triggers: PlaybookTrigger[] = [DEFAULT_SAVE_TRIGGER],
  now?: number,
): PlaybookEnrollment[] {
  const riskById = new Map(riskResults.map((r) => [r.accountId, r]));
  const parsed = triggers
    .map((t) => ({ trigger: t, clauses: parseCondition(t.condition) }))
    .filter((t): t is { trigger: PlaybookTrigger; clauses: TriggerClause[] } => t.clauses !== null);

  const enrollments: PlaybookEnrollment[] = [];
  for (const account of accounts) {
    const risk = riskById.get(account.id);
    if (!risk) continue; // no measured signal — never fabricate one

    const at = now ?? risk.computedAt;
    const renewalAt = risk.renewalAt ?? account.renewalAt;
    const metrics: TriggerMetrics = {
      churnProbability: risk.churnProbability,
      renewalDays:
        renewalAt !== undefined ? Math.max(0, Math.round((renewalAt - at) / DAY_MS)) : null,
      confidence: risk.confidence,
      leadTimeDays: risk.leadTimeDays,
      arrAtRisk: risk.accountValue ?? account.accountValue ?? 0,
    };

    for (const { trigger, clauses } of parsed) {
      const matched: MatchedClause[] = [];
      let all = true;
      for (const clause of clauses) {
        const actual = metricOf(metrics, clause.field);
        if (actual === null || !clauseHolds(clause, actual)) {
          all = false;
          break;
        }
        matched.push({ ...clause, actual });
      }
      if (!all) continue;

      const clauseText = matched
        .map((c) => `${c.field} ${c.actual}${c.op}${c.threshold}`)
        .join(', ');
      enrollments.push({
        id: `${trigger.playbookId}:${account.id}`,
        playbookId: trigger.playbookId,
        accountId: account.id,
        accountName: risk.accountName ?? account.name,
        triggerId: trigger.id,
        condition: trigger.condition,
        matched,
        churnProbability: risk.churnProbability,
        renewalDays: metrics.renewalDays,
        arrAtRisk: metrics.arrAtRisk,
        confidence: risk.confidence,
        enrolledAt: at,
        reason: `Auto-enrolled by '${trigger.condition}' — ${clauseText}${
          metrics.arrAtRisk > 0 ? ` · ${usd(metrics.arrAtRisk)} ARR at risk` : ''
        }`,
        source: risk.narrativeSource,
      });
      break; // one enrollment per account
    }
  }
  return enrollments;
}

// ===========================================================================
// buildPlaybookRun — governed, reversible steps
// ===========================================================================

export type SaveStepKind = 'save-email' | 'task' | 'exec-escalation';

/** Blast radius per step kind (0–100): how far the write reaches. */
export const STEP_BLAST_RADIUS: Record<SaveStepKind, number> = {
  'save-email': 35, // an external draft, but reversible + human-gated
  task: 15, // internal to-do — smallest reach
  'exec-escalation': 75, // pulls an exec into the account — widest reach
};

/** Ceiling at/below which a reversible, confident step is one-gesture safe. */
export const LOW_RISK_BLAST_CEILING = 40;

/** Deterministic task-title prefixes per step kind (dedupe + ledger keys). */
export const STEP_TITLE_PREFIX: Record<SaveStepKind, string> = {
  'save-email': 'Save email — ',
  task: 'Renewal check-in — ',
  'exec-escalation': 'Exec escalation — ',
};

/** One governed step of a playbook run — always a reversible action. */
export interface SavePlaybookStep {
  /** Deterministic: `${enrollmentId}:${kind}`. */
  id: string;
  runId: string;
  playbookId: string;
  accountId: string;
  accountName: string;
  kind: SaveStepKind;
  title: string;
  /**
   * The concrete governed action. Always `create_task` — the one action
   * kind whose executor (actions.runActionWithUndo) captures a real undo
   * (deleteTask), so every step is provably reversible.
   */
  action: CreateTaskAction;
  reversible: true;
  /** 0–100 write reach, from {@link STEP_BLAST_RADIUS}. */
  blastRadius: number;
  /** Carried from the enrollment's risk confidence, 0–1. */
  confidence: number;
  /** Step ids that must land first (taskGraph dependency edges). */
  blockedBy: string[];
}

/** One assembled, simulatable, reversible playbook run for one account. */
export interface PlaybookRun {
  /** Deterministic: `run:${enrollment.id}`. */
  id: string;
  enrollment: PlaybookEnrollment;
  steps: SavePlaybookStep[];
  /** Dependency order from the shipped task-graph engine (topoSort). */
  stepOrder: string[];
  /** Always false for the shipped step template; carried for honesty. */
  hasCycle: boolean;
  reversible: true;
}

/**
 * Assemble the governed churn-save steps for one enrollment: draft the save
 * email, then the renewal check-in call, then the exec escalation — each a
 * reversible `create_task` action chained by `blockedBy` edges and ordered
 * by the shipped task-graph engine. Pure; nothing is written.
 */
export function buildPlaybookRun(enrollment: PlaybookEnrollment): PlaybookRun {
  const runId = `run:${enrollment.id}`;
  const value = enrollment.arrAtRisk > 0 ? enrollment.arrAtRisk : undefined;
  const tie = { accountId: enrollment.accountId, accountName: enrollment.accountName };

  const mk = (
    kind: SaveStepKind,
    priority: CreateTaskAction['priority'],
    blockedBy: string[],
  ): SavePlaybookStep => {
    const title = `${STEP_TITLE_PREFIX[kind]}${enrollment.accountName}`;
    return {
      id: `${enrollment.id}:${kind}`,
      runId,
      playbookId: enrollment.playbookId,
      accountId: enrollment.accountId,
      accountName: enrollment.accountName,
      kind,
      title,
      action: { kind: 'create_task', title, priority, dueDate: null, value, ...tie },
      reversible: true,
      blastRadius: STEP_BLAST_RADIUS[kind],
      confidence: enrollment.confidence,
      blockedBy,
    };
  };

  const email = mk('save-email', 'urgent', []);
  const checkin = mk('task', 'high', [email.id]);
  const escalation = mk('exec-escalation', 'urgent', [checkin.id]);
  const steps = [email, checkin, escalation];

  // Dependency order via the SHIPPED task-graph engine — never re-derived.
  const graph = topoSort(
    steps.map(
      (s) =>
        ({
          id: s.id,
          title: s.title,
          status: 'todo',
          priority: s.action.priority ?? 'medium',
          assignee: null,
          dueDate: null,
          recurrence: 'none',
          checklist: [],
          blockedBy: s.blockedBy,
        }) as unknown as ProjectTask,
    ),
  );

  return {
    id: runId,
    enrollment,
    steps,
    stepOrder: graph.order,
    hasCycle: graph.hasCycle,
    reversible: true,
  };
}

// ===========================================================================
// simulateBatch — the full write-set, blast-radius-replayed, carve-out aware
// ===========================================================================

/** One row of the simulated write-set. */
export interface WriteSetEntry {
  runId: string;
  stepId: string;
  accountId: string;
  accountName: string;
  kind: SaveStepKind;
  /** old→new diff line via the shipped actionDiff; creates show '+ …'. */
  diff: string;
  /** True when a carve-out excluded this write from the batch. */
  excluded: boolean;
}

/** The pre-run simulation of an entire batch — nothing has been written. */
export interface BatchSimulation {
  writes: WriteSetEntry[];
  /** Writes that WOULD execute (carve-outs removed). */
  included: number;
  /** Writes removed by carve-outs. */
  excluded: number;
  /** Distinct accounts still in the batch. */
  accounts: number;
  /** Which save-email drafts the batch would create (by title). */
  emails: string[];
  /** Which internal tasks the batch would create (by title). */
  tasks: string[];
  /** Which contacts/accounts the exec escalations would pull in. */
  contacts: string[];
  /** Sum of included enrollments' real ARR at risk. */
  totalArrAtRisk: number;
  /** How the current autonomy policy routes the included steps. */
  routing: SimCounts;
  /** all-ask baseline vs the given policy — what would auto-fire. */
  blast: PolicyDelta;
  /** True when any step would newly auto-fire (needs explicit confirm). */
  saveBlocked: boolean;
  /** Deterministic one-line summary for the confirm surface. */
  summary: string;
}

export interface SimulateBatchOptions {
  /** Autonomy policy to route against. Default: everything asks a human. */
  policy?: AutonomyPolicy;
  /** Account ids / step ids / run ids to carve out of the batch. */
  carveOuts?: string[];
}

function stepToProposal(step: SavePlaybookStep): ScoredProposal {
  return {
    id: step.id,
    agentId: PLAYBOOK_ID,
    actionType: step.action.kind,
    impact: step.action.value ?? 0,
  };
}

/**
 * Simulate the entire batch BEFORE anything runs: the full write-set (which
 * emails / tasks / contacts would be touched, per account), routed through
 * the shipped policy simulator, with the blast-radius replay comparing the
 * all-ask baseline against the caller's policy so anything that would
 * auto-fire is surfaced (and gates the run via `saveBlocked`). Carve-outs
 * exclude an account's (or a single step's) writes without rebuilding the
 * batch. Pure projection — nothing is written.
 */
export function simulateBatch(
  runs: PlaybookRun[],
  opts: SimulateBatchOptions = {},
): BatchSimulation {
  const policy = opts.policy ?? emptyPolicy();
  const carved = new Set(opts.carveOuts ?? []);

  const writes: WriteSetEntry[] = [];
  const includedSteps: SavePlaybookStep[] = [];
  const emails: string[] = [];
  const tasks: string[] = [];
  const contacts: string[] = [];
  const includedAccounts = new Set<string>();
  let totalArrAtRisk = 0;

  for (const run of runs) {
    const runCarved = carved.has(run.id) || carved.has(run.enrollment.accountId);
    let anyIncluded = false;
    for (const step of run.steps) {
      const excluded = runCarved || carved.has(step.id);
      writes.push({
        runId: run.id,
        stepId: step.id,
        accountId: step.accountId,
        accountName: step.accountName,
        kind: step.kind,
        diff: diffForAction(step.action, []) ?? `+ ${step.action.kind} “${step.title}”`,
        excluded,
      });
      if (excluded) continue;
      anyIncluded = true;
      includedSteps.push(step);
      includedAccounts.add(step.accountId);
      if (step.kind === 'save-email') emails.push(step.title);
      else if (step.kind === 'task') tasks.push(step.title);
      else contacts.push(step.accountName);
    }
    if (anyIncluded) totalArrAtRisk += run.enrollment.arrAtRisk;
  }

  const proposals = includedSteps.map(stepToProposal);
  const sim = simulatePolicy(policy, proposals);
  const routing: SimCounts = {
    autoApprove: sim.autoApprove,
    needsHuman: sim.needsHuman,
    block: sim.block,
  };

  // Blast-radius replay via the shipped engine: baseline (all-ask) vs the
  // caller's policy — decide() delegates to the one true classifier.
  const decide = (p: ScoredProposal, pol: AutonomyPolicy): TriageOutcome =>
    classifyProposal(pol, p) === 'auto' ? 'auto-approve' : 'require-approval';
  const blast = computeBlastRadius(proposals, emptyPolicy(), policy, decide);

  const included = includedSteps.length;
  const excluded = writes.length - included;
  return {
    writes,
    included,
    excluded,
    accounts: includedAccounts.size,
    emails,
    tasks,
    contacts,
    totalArrAtRisk,
    routing,
    blast,
    saveBlocked: isSaveBlocked(blast),
    summary: `${included} write${included === 1 ? '' : 's'} across ${includedAccounts.size} account${
      includedAccounts.size === 1 ? '' : 's'
    }${excluded > 0 ? ` (${excluded} carved out)` : ''} · ${usd(totalArrAtRisk)} ARR at risk · ${blastRadiusSummary(blast)}`,
  };
}

// ===========================================================================
// triageBatch — blast-radius / reversibility / confidence ranking
// ===========================================================================

/** One triaged step with its composite rank key + rationale. */
export interface TriagedStep {
  step: SavePlaybookStep;
  /** Blast radius dominates; irreversibility and low confidence penalize. */
  triageScore: number;
  /** True when reversible, low-blast, and confident enough to batch-approve. */
  lowRisk: boolean;
  rationale: string;
}

/** The triaged batch: full ranking plus the one-gesture / review splits. */
export interface TriagedActions {
  /** Highest triage score (widest blast) first — review these first. */
  ranked: TriagedStep[];
  /** Reversible, low-blast, confident steps — safe for one-gesture approval. */
  lowRisk: TriagedStep[];
  /** Everything else (wide blast or shaky confidence) — per-item review. */
  needsReview: TriagedStep[];
}

/**
 * Rank every step of the batch by blast radius (dominant), reversibility,
 * and confidence, then split into the one-gesture low-risk set vs the
 * needs-review set. High-blast exec escalations always outrank low-risk
 * sends. Pure and deterministic (stable sort; ties keep run order).
 */
export function triageBatch(runs: PlaybookRun[]): TriagedActions {
  const triaged: TriagedStep[] = runs
    .flatMap((r) => r.steps)
    .map((step) => {
      const irreversiblePenalty = step.reversible ? 0 : 500;
      const confidencePenalty = Math.round((1 - step.confidence) * 100);
      const triageScore = step.blastRadius * 1000 + irreversiblePenalty + confidencePenalty;
      const lowRisk =
        step.reversible && step.blastRadius <= LOW_RISK_BLAST_CEILING && step.confidence >= 0.5;
      return {
        step,
        triageScore,
        lowRisk,
        rationale: `blast ${step.blastRadius}/100 · ${
          step.reversible ? 'reversible' : 'IRREVERSIBLE'
        } · confidence ${Math.round(step.confidence * 100)}%`,
      };
    });

  const ranked = [...triaged].sort((a, b) => b.triageScore - a.triageScore);
  return {
    ranked,
    lowRisk: ranked.filter((t) => t.lowRisk),
    needsReview: ranked.filter((t) => !t.lowRisk),
  };
}

// ===========================================================================
// resolveAutonomy — the earned per-account / per-playbook trust ladder
// ===========================================================================

/** Clean approvals required before a step class earns auto-send. */
export const AUTONOMY_CLEAN_APPROVALS = 5;

/** A ledger event scoped to this playbook (optionally to one account). */
export interface SaveLedgerEntry extends EarnUpLedgerEntry {
  /** When set, the event counts only toward that account's ladder. */
  accountId?: string;
}

/** The resolved autonomy for one step: stay human, or earned auto-send. */
export interface AutonomyGrant {
  stepId: string;
  accountId: string;
  playbookId: string;
  /** The trust-ladder class: `${playbookId}:${stepKind}`. */
  actionClass: string;
  mode: 'human-approval' | 'auto-send';
  /** Clean (unedited, unreversed) approvals counted for the class. */
  cleanApprovals: number;
  required: number;
  /** Auto-send is NEVER unsupervised: post-hoc review always rides along. */
  postHocReview: boolean;
  reason: string;
}

/** The trust-ladder class a step's history is counted under. */
export function autonomyClassFor(step: Pick<SavePlaybookStep, 'playbookId' | 'kind'>): string {
  return `${step.playbookId}:${step.kind}`;
}

/**
 * Resolve a step's autonomy from sealed ledger history: after
 * {@link AUTONOMY_CLEAN_APPROVALS} clean approvals of the same class (for
 * this account, or playbook-wide entries carrying no accountId) the step
 * earns auto-send with mandatory post-hoc review. ANY reversal, edit, or
 * reject in the class history keeps it human-approved — a single breach is
 * never papered over by a high approval count (same rule as the shipped
 * earn-up engine, whose stats math is reused verbatim). Empty history →
 * human-approval, always.
 */
export function resolveAutonomy(
  step: SavePlaybookStep,
  ledgerHistory: SaveLedgerEntry[],
): AutonomyGrant {
  const actionClass = autonomyClassFor(step);
  const matched = ledgerHistory.filter(
    (e) =>
      e.actionClass === actionClass &&
      (e.accountId === undefined || e.accountId === step.accountId),
  );
  const stats = computeActionClassStats(matched).find((s) => s.actionClass === actionClass);
  const clean = stats?.approved ?? 0;
  const total = stats?.total ?? 0;
  const dirty = total - clean; // edits + reversals + rejects all reset trust

  const base = {
    stepId: step.id,
    accountId: step.accountId,
    playbookId: step.playbookId,
    actionClass,
    cleanApprovals: clean,
    required: AUTONOMY_CLEAN_APPROVALS,
  };

  if (dirty > 0) {
    return {
      ...base,
      mode: 'human-approval',
      postHocReview: false,
      reason: `Trust reset: ${dirty} non-clean event${dirty === 1 ? '' : 's'} (edit/reversal/reject) in ${actionClass} history — human approval required`,
    };
  }
  if (clean >= AUTONOMY_CLEAN_APPROVALS) {
    return {
      ...base,
      mode: 'auto-send',
      postHocReview: true,
      reason: `${clean} clean approvals ≥ ${AUTONOMY_CLEAN_APPROVALS} for ${actionClass} — auto-send earned, post-hoc review required`,
    };
  }
  return {
    ...base,
    mode: 'human-approval',
    postHocReview: false,
    reason: `${clean}/${AUTONOMY_CLEAN_APPROVALS} clean approvals for ${actionClass} — human approval required`,
  };
}

/**
 * Adapt resolved approvals-inbox rows into trust-ladder ledger entries by
 * matching the playbook's deterministic step-title prefixes. Approved →
 * 'approve'; dismissed → 'reject'; pending rows are intent, not history.
 * Pure — the caller owns the read.
 */
export function ledgerEntriesFromApprovals(approvals: ApprovalItem[]): SaveLedgerEntry[] {
  const kinds = Object.keys(STEP_TITLE_PREFIX) as SaveStepKind[];
  const entries: SaveLedgerEntry[] = [];
  for (const item of approvals) {
    if (item.status === 'pending') continue;
    if (item.action.kind !== 'create_task') continue;
    const action = item.action as CreateTaskAction;
    const kind = kinds.find((k) => action.title.startsWith(STEP_TITLE_PREFIX[k]));
    if (!kind) continue;
    const entry: SaveLedgerEntry = {
      id: item.id,
      actionClass: `${PLAYBOOK_ID}:${kind}`,
      event: item.status === 'approved' ? 'approve' : 'reject',
      at: item.resolvedAt ?? item.createdAt,
    };
    if (action.accountId) entry.accountId = action.accountId;
    entries.push(entry);
  }
  return entries;
}

// ===========================================================================
// verifyPlayOutcome — closed-loop save verification + value attribution
// ===========================================================================

/** Minimum health-delta a run must produce to count as a save. */
export const SAVE_HEALTH_DELTA_TARGET = 1;

/** A measured health reading at a point in time. */
export interface HealthReading {
  /** 0–100 health score (higher = healthier). */
  health: number;
  /** Epoch ms the reading was taken. */
  at: number;
}

/** The verified outcome of one playbook run — the loop, closed. */
export interface PlayOutcome {
  runId: string;
  accountId: string;
  accountName: string;
  /** True only when the shipped verifier rendered VERIFIED. */
  saved: boolean;
  verdict: OutcomeVerdict;
  healthBefore: number;
  healthAfter: number;
  healthDelta: number;
  /** Real ARR retained when saved; 0 otherwise — never invented. */
  arrRetained: number;
  /** '$84,000 ARR retained — Acme health 42 → 68'; null when not saved. */
  attribution: string | null;
  /** Value-ledger entry to seal when saved (status 'approved'); else null. */
  valueEntry: ValueEntry | null;
  /** The sealed after-verdict payload for the decision ledger. */
  verdictEntry: OutcomeVerdictEntry;
  /** Verifier evidence lines — the audit trail. */
  evidence: string[];
}

function usd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Close the loop on one run: did the account's health ACTUALLY recover?
 * The verdict is delegated to the shipped outcome verifier over a
 * health_delta contract spanning [before.at, after.at] — never re-derived
 * here. A VERIFIED recovery marks the account saved and produces the
 * value-ledger attribution ('$84,000 ARR retained') as plain data for the
 * caller to seal; a miss (or an unclosed window) earns nothing. Pure.
 */
export function verifyPlayOutcome(
  run: PlaybookRun,
  before: HealthReading,
  after: HealthReading,
  now: number = after.at,
): PlayOutcome {
  const arr = run.enrollment.arrAtRisk;
  const contract = buildOutcomeContract(
    { id: run.id, action: { kind: 'create_task', value: arr } },
    {
      kind: 'health_delta',
      now: before.at,
      windowMs: Math.max(1, after.at - before.at),
      target: SAVE_HEALTH_DELTA_TARGET,
    },
  );
  const result = verifyOutcome(contract, {
    activity: [],
    healthAt: (t) => (t <= before.at ? before.health : t >= after.at ? after.health : undefined),
    now,
  });

  const saved = result.verdict === 'VERIFIED';
  const delta = after.health - before.health;
  const arrRetained = saved ? arr : 0;
  const attribution = saved
    ? arr > 0
      ? `${usd(arr)} ARR retained — ${run.enrollment.accountName} health ${before.health} → ${after.health}`
      : `${run.enrollment.accountName} health recovered ${before.health} → ${after.health} (no ARR on record)`
    : null;
  const valueEntry: ValueEntry | null = saved
    ? {
        id: `outcome:${run.id}`,
        agent: PLAYBOOK_ID,
        at: new Date(now).toISOString(),
        status: 'approved',
        minutesSaved: 0,
        dollarImpact: arrRetained,
      }
    : null;

  return {
    runId: run.id,
    accountId: run.enrollment.accountId,
    accountName: run.enrollment.accountName,
    saved,
    verdict: result.verdict,
    healthBefore: before.health,
    healthAfter: after.health,
    healthDelta: delta,
    arrRetained,
    attribution,
    valueEntry,
    verdictEntry: verdictToLedgerEntry(result, run.id),
    evidence: result.evidence,
  };
}

// ===========================================================================
// rollupPlaybookEffectiveness — provable retention conversion
// ===========================================================================

/** The conversion stat a renewal deck needs: 'saved 8 of 11'. */
export interface PlaybookEffectiveness {
  saved: number;
  atRisk: number;
  /** e.g. 'saved 8 of 11'. */
  savedOfAtRisk: string;
  /** saved / atRisk (0 when no outcomes exist — never NaN). */
  conversionRate: number;
  /** Sum of real ARR retained across saved outcomes. */
  arrRetained: number;
}

/**
 * Roll up verified outcomes into the honest conversion stat. Every outcome
 * counts in the denominator (an unsaved at-risk account is a real miss);
 * strict zero-state: no outcomes → all zeros, never NaN.
 */
export function rollupPlaybookEffectiveness(outcomes: PlayOutcome[]): PlaybookEffectiveness {
  const atRisk = outcomes.length;
  let saved = 0;
  let arrRetained = 0;
  for (const o of outcomes) {
    if (!o.saved) continue;
    saved += 1;
    arrRetained += o.arrRetained;
  }
  return {
    saved,
    atRisk,
    savedOfAtRisk: `saved ${saved} of ${atRisk}`,
    conversionRate: atRisk === 0 ? 0 : saved / atRisk,
    arrRetained,
  };
}
